/**
 * NodeMenu - Slide-out panel listing available nodes for drag-drop
 *
 * Supports collapsible tree structure:
 * - Domain (Data, Audio, Video)
 *   - Namespace (e.g., hydra)
 *     - Category (e.g., source, effect)
 *       - Nodes
 */

import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { useAppInit } from '../contexts/AppInit';
import { useRuntime } from '../contexts/RuntimeProvider';
import { definitionService } from '@/serverClient/definitionService';
import { Tooltip } from '@/ui/shared/Tooltip';

interface NodeMenuProps {
  isOpen: boolean;
  onToggle: () => void;
}

interface DraggableNodeProps {
  nodeType: string;
  label: string;
  info?: string;
  isDefinition?: boolean;
}

function DraggableNode({ nodeType, label, info, isDefinition }: DraggableNodeProps) {
  const onDragStart = (event: React.DragEvent) => {
    event.dataTransfer.setData('application/reactflow-type', nodeType);
    event.dataTransfer.setData('application/reactflow-is-definition', isDefinition ? 'true' : 'false');
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <Tooltip text={info} placement="right">
      <div
        draggable
        onDragStart={onDragStart}
        style={styles.nodeItem}
      >
        <span style={styles.nodeIcon}>{isDefinition ? '📦' : '⬡'}</span>
        <span style={styles.nodeLabel}>{label}</span>
      </div>
    </Tooltip>
  );
}

function DraggableField({ fieldType, label }: { fieldType: string; label: string }) {
  const onDragStart = (event: React.DragEvent) => {
    event.dataTransfer.setData('application/field-type', fieldType);
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div
      draggable
      onDragStart={onDragStart}
      style={styles.nodeItem}
    >
      <span style={styles.nodeIcon}>◧</span>
      <span style={styles.nodeLabel}>{label}</span>
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  level: number;
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

function CollapsibleSection({ title, level, children, defaultExpanded = true }: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  const indent = level * 8;

  // Style based on level:
  // Level 0 (Domain): bold, bright
  // Level 1 (Namespace): medium weight, visible
  // Level 2 (Category): slightly smaller but still readable
  const levelStyles = {
    0: { fontSize: 11, color: '#e5e7eb', fontWeight: 600 as const },
    1: { fontSize: 10, color: '#d1d5db', fontWeight: 500 as const },
    2: { fontSize: 10, color: '#9ca3af', fontWeight: 500 as const }
  };
  const style = levelStyles[level as keyof typeof levelStyles] ?? levelStyles[2];

  return (
    <div style={{ marginBottom: level === 0 ? 8 : 2 }}>
      <div
        onClick={toggleExpanded}
        style={{
          ...styles.sectionHeader,
          paddingLeft: 12 + indent,
          fontSize: style.fontSize,
          color: style.color,
          fontWeight: style.fontWeight
        }}
      >
        <span style={styles.chevron}>{isExpanded ? '▼' : '▶'}</span>
        <span>{title}</span>
      </div>
      {isExpanded && (
        <div style={{ marginLeft: indent }}>
          {children}
        </div>
      )}
    </div>
  );
}

// Parse node type to extract namespace and base type
function parseNodeType(type: string): { namespace: string | null; baseType: string } {
  const dotIndex = type.indexOf('.');
  if (dotIndex !== -1) {
    return {
      namespace: type.substring(0, dotIndex),
      baseType: type.substring(dotIndex + 1)
    };
  }
  return { namespace: null, baseType: type };
}

interface NodeInfo {
  type: string;
  baseType: string;
  shortName?: string;
  label?: string;  // Display label for UI
  info?: string;   // Help text shown in tooltip
  namespace: string | null;
  category?: string;
  keywords?: string[];  // Additional search terms
}

export function NodeMenu({ isOpen, onToggle }: NodeMenuProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeDomain, setActiveDomain] = useState<string | null>(null); // null = show all
  const searchInputRef = useRef<HTMLInputElement>(null);
  const { menu } = useAppInit();
  const { definitionState } = useRuntime();

  // Track library definitions, refreshing when definitionService notifies of changes
  const [libDefIds, setLibDefIds] = useState<string[]>(menu.libDefIds);

  useEffect(() => {
    // Initialize from menu
    setLibDefIds(menu.libDefIds);

    // Subscribe to definition saves
    const unsubscribe = definitionService.subscribe(async () => {
      const definitions = await definitionService.getAll();
      setLibDefIds(Object.keys(definitions));
    });

    return unsubscribe;
  }, [menu.libDefIds]);

  // Autofocus search input when menu opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Derive project definitions from definitionState (exclude root and library definitions)
  const projectDefIds = useMemo(() => {
    const libIds = new Set(libDefIds);
    return Object.keys(definitionState).filter(id => id !== 'root' && !libIds.has(id));
  }, [definitionState, libDefIds]);

  // Library definitions that are also in the project (have been used/modified)
  const libraryDefsInProject = useMemo(() => {
    const libIds = new Set(libDefIds);
    return new Set(Object.keys(definitionState).filter(id => libIds.has(id)));
  }, [definitionState, libDefIds]);

  // Build hierarchical structure by domain -> namespace -> category
  const nodesByDomain = useMemo(() => {
    const result: Record<string, Record<string, Record<string, NodeInfo[]>>> = {};

    for (const { type, shortName, label, info, domain, category, keywords } of menu.primitiveDefs) {
      const { namespace, baseType } = parseNodeType(type);
      const domainKey = domain ?? 'data';
      const namespaceKey = namespace ?? '_root';
      const categoryKey = category ?? '_uncategorized';

      if (!result[domainKey]) result[domainKey] = {};
      if (!result[domainKey][namespaceKey]) result[domainKey][namespaceKey] = {};
      if (!result[domainKey][namespaceKey][categoryKey]) result[domainKey][namespaceKey][categoryKey] = [];

      result[domainKey][namespaceKey][categoryKey].push({
        type,
        baseType,
        shortName,
        label,
        info,
        namespace,
        category,
        keywords
      });
    }

    return result;
  }, [menu.primitiveDefs]);

  // Filter nodes by search term (matches type, baseType, shortName, label, category, or keywords)
  // Keywords support partial word matching: "geo" matches "geometry"
  const filterNodes = useCallback((nodes: NodeInfo[]) => {
    if (!searchTerm) return nodes;
    const term = searchTerm.toLowerCase();
    return nodes.filter(node => {
      // Direct matches on type, baseType, shortName, label, category
      if (node.type.toLowerCase().includes(term)) return true;
      if (node.baseType.toLowerCase().includes(term)) return true;
      if (node.shortName?.toLowerCase().includes(term)) return true;
      if (node.label?.toLowerCase().includes(term)) return true;
      if (node.category?.toLowerCase().includes(term)) return true;
      // Partial match on keywords: "geo" matches keyword "geometry"
      if (node.keywords?.some(kw => kw.toLowerCase().includes(term))) return true;
      return false;
    });
  }, [searchTerm]);

  const filteredLibraryDefinitions = useMemo(() =>
    libDefIds.filter(id => id.toLowerCase().includes(searchTerm.toLowerCase())),
    [libDefIds, searchTerm]
  );

  const filteredProjectDefinitions = useMemo(() =>
    projectDefIds.filter(id => id.toLowerCase().includes(searchTerm.toLowerCase())),
    [projectDefIds, searchTerm]
  );

  // Domain display order and labels
  const domainOrder = ['data', 'audio', 'video'];
  const domainLabels: Record<string, string> = {
    data: 'Data',
    audio: 'Audio',
    video: 'Video'
  };

  // Derive unique categories from nodes and build display order
  const { categoryLabels, categoryOrder } = useMemo(() => {
    const categories = new Set<string>();

    // Collect all categories from nodes
    for (const domainData of Object.values(nodesByDomain)) {
      for (const namespaceData of Object.values(domainData)) {
        for (const category of Object.keys(namespaceData)) {
          categories.add(category);
        }
      }
    }

    // Build labels: title case for display, special case for _uncategorized
    const labels: Record<string, string> = {};
    for (const cat of categories) {
      if (cat === '_uncategorized') {
        labels[cat] = 'General';
      } else {
        labels[cat] = cat.charAt(0).toUpperCase() + cat.slice(1);
      }
    }

    // Build order: known categories first (for consistent UI), then alphabetically
    const knownOrder = ['source', 'effect', 'composite', 'modulate', 'output'];
    const order = knownOrder.filter(c => categories.has(c));
    const remaining = [...categories]
      .filter(c => !knownOrder.includes(c) && c !== '_uncategorized')
      .sort();
    order.push(...remaining);
    if (categories.has('_uncategorized')) {
      order.push('_uncategorized');
    }

    return { categoryLabels: labels, categoryOrder: order };
  }, [nodesByDomain]);

  // Check if any nodes match search in a namespace
  const hasMatchingNodes = useCallback((namespaceData: Record<string, NodeInfo[]>) => {
    for (const nodes of Object.values(namespaceData)) {
      if (filterNodes(nodes).length > 0) return true;
    }
    return false;
  }, [filterNodes]);

  // Check if any nodes match search in a domain
  const hasMatchingInDomain = useCallback((domainData: Record<string, Record<string, NodeInfo[]>>) => {
    for (const namespaceData of Object.values(domainData)) {
      if (hasMatchingNodes(namespaceData)) return true;
    }
    return false;
  }, [hasMatchingNodes]);

  return (
    <>
      {/* Toggle button */}
      <button
        onClick={onToggle}
        style={{
          ...styles.toggleButton,
          left: isOpen ? 250 : 0
        }}
        title={isOpen ? 'Close node menu' : 'Open node menu'}
      >
        {isOpen ? '◀' : '▶'}
      </button>

      {/* Panel */}
      <div style={{
        ...styles.panel,
        transform: isOpen ? 'translateX(0)' : 'translateX(-100%)'
      }}>
        {/* Domain filter tabs */}
        <div style={styles.domainTabs}>
          {domainOrder.map(domain => {
            const isActive = activeDomain === domain;
            const hasNodes = nodesByDomain[domain] && hasMatchingInDomain(nodesByDomain[domain]);
            if (!hasNodes && !isActive) return null;
            return (
              <button
                key={domain}
                onClick={() => setActiveDomain(isActive ? null : domain)}
                style={{
                  ...styles.domainTab,
                  background: isActive ? '#4b5563' : 'transparent',
                  color: isActive ? '#e5e7eb' : '#9ca3af'
                }}
              >
                {domainLabels[domain]}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={styles.searchContainer}>
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Search nodes..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
        </div>

        {/* Node list */}
        <div style={styles.content}>
          {/* Primitives by domain -> namespace -> category */}
          {domainOrder.map(domain => {
            // Filter by active domain if one is selected
            if (activeDomain && domain !== activeDomain) return null;

            const domainData = nodesByDomain[domain];
            if (!domainData || !hasMatchingInDomain(domainData)) return null;

            const namespaces = Object.keys(domainData);

            return (
              <CollapsibleSection key={domain} title={domainLabels[domain]} level={0}>
                {namespaces.map(namespace => {
                  const namespaceData = domainData[namespace];
                  if (!hasMatchingNodes(namespaceData)) return null;

                  const categories = Object.keys(namespaceData);
                  const hasOnlyUncategorized = categories.length === 1 && categories[0] === '_uncategorized';

                  // If only _root namespace, don't show namespace level
                  if (namespace === '_root') {
                    // Render categories or nodes directly
                    if (hasOnlyUncategorized) {
                      // Just nodes, no nesting
                      return filterNodes(namespaceData._uncategorized).map(node => (
                        <DraggableNode key={node.type} nodeType={node.type} label={node.label ?? node.baseType} info={node.info} />
                      ));
                    }
                    // Multiple categories under root namespace
                    return categoryOrder.map(category => {
                      const nodes = namespaceData[category];
                      if (!nodes) return null;
                      const filtered = filterNodes(nodes);
                      if (filtered.length === 0) return null;
                      return (
                        <CollapsibleSection key={category} title={categoryLabels[category] ?? category} level={1}>
                          {filtered.map(node => (
                            <DraggableNode key={node.type} nodeType={node.type} label={node.label ?? node.baseType} info={node.info} />
                          ))}
                        </CollapsibleSection>
                      );
                    });
                  }

                  // Namespace with categories
                  return (
                    <CollapsibleSection
                      key={namespace}
                      title={namespace.charAt(0).toUpperCase() + namespace.slice(1)}
                      level={1}
                    >
                      {hasOnlyUncategorized ? (
                        // Just nodes under namespace
                        filterNodes(namespaceData._uncategorized).map(node => (
                          <DraggableNode key={node.type} nodeType={node.type} label={node.label ?? node.baseType} info={node.info} />
                        ))
                      ) : (
                        // Categories under namespace
                        categoryOrder.map(category => {
                          const nodes = namespaceData[category];
                          if (!nodes) return null;
                          const filtered = filterNodes(nodes);
                          if (filtered.length === 0) return null;
                          return (
                            <CollapsibleSection key={category} title={categoryLabels[category] ?? category} level={2}>
                              {filtered.map(node => (
                                <DraggableNode key={node.type} nodeType={node.type} label={node.label ?? node.baseType} info={node.info} />
                              ))}
                            </CollapsibleSection>
                          );
                        })
                      )}
                    </CollapsibleSection>
                  );
                })}
              </CollapsibleSection>
            );
          })}

          {/* Library Definitions */}
          {filteredLibraryDefinitions.length > 0 && (
            <CollapsibleSection title="Library" level={0}>
              {filteredLibraryDefinitions.map(id => (
                <DraggableNode
                  key={id}
                  nodeType={id}
                  label={libraryDefsInProject.has(id) ? `${id} *` : id}
                  isDefinition
                />
              ))}
            </CollapsibleSection>
          )}

          {/* Project Definitions */}
          {filteredProjectDefinitions.length > 0 && (
            <CollapsibleSection title="Project" level={0}>
              {filteredProjectDefinitions.map(id => (
                <DraggableNode key={`proj-${id}`} nodeType={id} label={id} isDefinition />
              ))}
            </CollapsibleSection>
          )}

          {/* Fields */}
          {(!searchTerm || 'gradient'.includes(searchTerm.toLowerCase()) || 'field'.includes(searchTerm.toLowerCase())) && (
            <CollapsibleSection title="Fields" level={0}>
              <DraggableField fieldType="gradient" label="Gradient" />
            </CollapsibleSection>
          )}
        </div>

        {/* Help text */}
        <div style={styles.footer}>
          Drag nodes onto the canvas
        </div>
      </div>
    </>
  );
}

// Header height
const HEADER_HEIGHT = 60;

const styles: Record<string, React.CSSProperties> = {
  panel: {
    position: 'absolute',
    top: HEADER_HEIGHT,
    left: 0,
    bottom: 0,
    width: 250,
    background: '#1f2937',
    borderRight: '1px solid #374151',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 100,
    transition: 'transform 0.2s ease-out'
  },
  toggleButton: {
    position: 'absolute',
    top: `calc(50% + ${HEADER_HEIGHT / 2}px)`,
    transform: 'translateY(-50%)',
    width: 24,
    height: 48,
    background: '#374151',
    border: 'none',
    borderRadius: '0 4px 4px 0',
    color: '#9ca3af',
    cursor: 'pointer',
    zIndex: 101,
    transition: 'left 0.2s ease-out',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12
  },
  domainTabs: {
    display: 'flex',
    gap: 4,
    padding: '8px 12px',
    borderBottom: '1px solid #374151'
  },
  domainTab: {
    flex: 1,
    padding: '6px 8px',
    border: 'none',
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'all 0.15s'
  },
  searchContainer: {
    padding: '8px 12px',
    borderBottom: '1px solid #374151'
  },
  searchInput: {
    width: '100%',
    background: '#374151',
    border: '1px solid #4b5563',
    borderRadius: 4,
    padding: '6px 10px',
    color: 'white',
    fontSize: 12,
    outline: 'none'
  },
  content: {
    flex: 1,
    overflow: 'auto',
    padding: '8px 0'
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 12px',
    cursor: 'pointer',
    userSelect: 'none',
    textTransform: 'uppercase',
    letterSpacing: '0.05em'
  },
  chevron: {
    fontSize: 8,
    width: 10
  },
  nodeItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '6px 16px 6px 28px',
    cursor: 'grab',
    color: '#d1d5db',
    fontSize: 13,
    transition: 'background 0.1s'
  },
  nodeIcon: {
    fontSize: 12,
    opacity: 0.7
  },
  nodeLabel: {
    flex: 1
  },
  empty: {
    padding: '8px 16px',
    color: '#6b7280',
    fontSize: 12,
    fontStyle: 'italic'
  },
  footer: {
    padding: '12px 16px',
    borderTop: '1px solid #374151',
    fontSize: 11,
    color: '#6b7280',
    textAlign: 'center'
  }
};
